import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardNavs } from "../navigationSidepanel";
import { useAuth } from "../auth/AuthProvider";

interface NavItem {
  title: string;
  path?: string;
  subModuleKey?: string;
  children?: NavItem[];
}

type PermissionValue = string[] | Set<string>;

export const useModuleRedirect = (moduleKey: keyof typeof dashboardNavs) => {
  const { user, roleData } = useAuth();
  const navigate = useNavigate();

  const roles = user?.roles ?? [];
  const permissions = roleData?.permissions ?? {};

  const isAdmin = roles.some((role) =>
    ["orgadmin", "superadmin"].includes(role.toLowerCase())
  );

  useEffect(() => {

    if (!user) return;

    // permissions are still loading.
    if (!isAdmin && !roleData) return;

    const navItems: NavItem[] = dashboardNavs[moduleKey] ?? [];

    const hasReadPermission = (subModuleKey?: string): boolean => {
      if (!subModuleKey) return false;

      const subPermissions = (
        permissions as Record<string, PermissionValue>
      )[subModuleKey];

      if (Array.isArray(subPermissions)) {
        return subPermissions.includes("read");
      }

      if (subPermissions instanceof Set) {
        return subPermissions.has("read");
      }

      return false;
    };

    const findFirstAccessible = (items: NavItem[]): NavItem | undefined => {
      for (const item of items) {
        if (
          item.path &&
          (isAdmin || hasReadPermission(item.subModuleKey))
        ) {
          return item;
        }
        // Check children recursively
        if (item.children?.length) {
          const child = findFirstAccessible(item.children);
          if (child) return child;
        }
      }
      return undefined;
    };

    const firstAccessible = findFirstAccessible(navItems);

     navigate(firstAccessible?.path ?? "/unauthorized", {
      replace: true,
    });
  }, [moduleKey, user, roleData, permissions, isAdmin, navigate]);
};
